import { initCartActions } from '@/shared/instances/cart-button.js';
import { initHobbiiStarter } from '@/shared/instances/hobbii-starter';
import { initPatternDownload } from '@/shared/instances/pattern-download-button.js';
import { initQuantitySelector } from '@/shared/instances/quantity-selector.js';
import { initStickyAtcBar } from '@/shared/instances/sticky-atc-bar';
import { initWishlistActions } from '@/shared/instances/wishlist-button.js';
import { useProductStore } from '@/shared/pinia/modules/product';
import { trackProductViewed } from '@/shared/tracking/product-viewed';
import { getProductDataFromHTML } from '@/shared/utils/product';

import { initProductInfoTabs } from './helpers/product-info-tabs';
import {
  initDropdownOptions,
  initGalleryOptions,
  initSingleOption
} from './helpers/product-options.js';
import {
  initTabCounters,
  initYarnPatternsTabCount
} from './helpers/product-tab-async';
import { setReadMoreListeners } from './helpers/read-more';

const optionsMountId = 'js-product-options';
const cartActionsMountId = 'js-product-cart-actions';
const wishlistActionsMountId = 'js-product-wishlist-actions';
const quantityPickerMountId = 'js-product-quantity-selector';
const stickyAtcBarMountId = 'js-sticky-atc-bar';

// set option selector
const initOptionsSelector = () => {
  const elem = document.getElementById(optionsMountId);

  const optionKind = elem?.dataset.optionKind;
  const type = optionKind ? JSON.parse(optionKind) : null;

  switch (type?.layout) {
    case 'gallery':
      initGalleryOptions(optionsMountId);
      break;
    case 'dropdown':
      initDropdownOptions(optionsMountId);
      break;
    default: {
      initSingleOption(optionsMountId);
    }
  }
};

const initProductPageApps = async () => {
  const {
    productId,
    variantId,
    legacyId,
    productGroupId,
    entityType,
    pointCost,
    productMainImageUrl,
    productName
  } = getProductDataFromHTML();

  const productStore = useProductStore();

  if (entityType) {
    productStore.entityType = entityType;
  }
  if (productId) {
    productStore.productId = productId;
  }
  if (productName) {
    productStore.productName = productName;
  }
  if (variantId) {
    productStore.variantId = variantId;
  }
  if (legacyId) {
    productStore.legacyId = legacyId;
  }

  if (productGroupId) {
    productStore.productGroupId = productGroupId;
  }

  if (pointCost && Number(pointCost) > 0) {
    productStore.pointsPrice = Number(pointCost);
  }

  if (productMainImageUrl) {
    productStore.mainImageUrl = productMainImageUrl;
  }

  if (productStore.isPattern) {
    // TODO perhaps use hide / show on the product page instead of this
    initPatternDownload('pattern-download-button');

    const { isFreePattern } = getProductDataFromHTML();
    if (isFreePattern === 'true') {
      productStore.purchasable = false;
    }
  }

  if (productStore.isProduct) {
    initOptionsSelector();
  }
  initCartActions(cartActionsMountId);
  initWishlistActions(wishlistActionsMountId);
  initQuantitySelector(quantityPickerMountId);
  void initProductInfoTabs();
  initTabCounters();
  initYarnPatternsTabCount();

  setReadMoreListeners();

  // Track product viewed event
  trackProductViewed();

  // Sticky ATC bar (mobile only)
  if (!productStore.isPattern) {
    const el = document.getElementById(stickyAtcBarMountId);
    if (el) {
      el.classList.remove('hidden');
      initStickyAtcBar(stickyAtcBarMountId);
    }
  }
};

const initProductPageGlobals = () => {
  const el = document.getElementById('product-data');
  if (!el || !window.Hobbii) return;
  window.Hobbii.productPage = {
    productId: Number(el.dataset.productId),
    targetVariantId: Number(el.dataset.variantId),
    isPattern: el.dataset.isPattern === 'true'
  };
  window.dataLayer?.push({
    productGroupId: el.dataset.productGroupId,
    productUuid: el.dataset.entityId
  });
};

initProductPageGlobals();
initHobbiiStarter();
initProductPageApps();
